/**
 * The one thing this browser remembers between gifts: the name the donor chose to be recognised
 * by.
 *
 * Scope is deliberately tiny. No amount, no email, no phone, no organization, no history — a
 * single string, so that a returning donor does not retype their name, and nothing else about
 * their giving is reconstructable from this device.
 *
 * ## The shared-device problem
 *
 * A QR code at an event is often scanned on a phone that belongs to one person, but this bundle
 * can also be opened on an organization tablet passed between people. Two properties keep that from
 * leaking one donor's name to the next:
 *
 *   1. **Anonymous stays the default.** The stored name is never applied unless the donor
 *      deliberately turns recognition on, so a second person on the same device sees an anonymous
 *      form exactly as the first did.
 *   2. **Turning anonymity back on forgets it.** Choosing to be anonymous is treated as a choice
 *      about this device too, not only about this gift.
 *
 * That is a mitigation, not a fix. A shared device where someone turns recognition on will show
 * the previous donor's name in an editable field. `docs/decision-register.md` records this under
 * DEC-011 as the reason a kiosk build must disable this module outright.
 */

const NAME_KEY = "givehere.donor.displayName";
const MAX_NAME_LENGTH = 80;

/**
 * Storage can throw rather than return null — Safari's private mode and a blocked third-party
 * context both do. A donation must never fail because a convenience feature could not read a
 * string, so every access is wrapped and every failure degrades to "we remember nothing".
 */
function safeStorage(): Storage | null {
  try {
    const storage = globalThis.localStorage;
    // Touch it: merely having the property does not mean access is permitted.
    const probe = "__givehere_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

export function rememberedDisplayName(): string {
  const storage = safeStorage();
  if (!storage) return "";
  try {
    return (storage.getItem(NAME_KEY) ?? "").slice(0, MAX_NAME_LENGTH);
  } catch {
    return "";
  }
}

/** Called only when the donor has chosen to be recognised. Anonymous gifts store nothing. */
export function rememberDisplayName(name: string): void {
  const trimmed = name.trim().slice(0, MAX_NAME_LENGTH);
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (trimmed.length === 0) storage.removeItem(NAME_KEY);
    else storage.setItem(NAME_KEY, trimmed);
  } catch {
    // Nothing to do and nothing to tell the donor: the gift is unaffected.
  }
}

export function forgetDisplayName(): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(NAME_KEY);
  } catch {
    // See above.
  }
}
